import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import queryString from 'query-string';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { Resolver as ConfigResolver, ServiceConfig } from '../../config';
import Log from '../../common/logging';
import cachedAxios from '../../utils/cachedAxios';
import { GknServiceData } from './GatekeeperNetworkServiceData';
import { formatCooldownPeriod } from '../../utils/date';
import { getGknValue, mergeAllChainSpecificSettings } from './utils';
import { isChainNetworkMainnet } from '../../utils/utils';
import { networkConfigs } from '../../common/types';

/**
 * Update the constraints object with any overrides from the Gatekeeper Network Service.
 * @param gknServiceDataResponse: GknServiceDataResponse
 * @param chainName: string
 * @param chainNetwork: string
 * @returns: GknServiceData
 */
export function updateConstraintOverrides(
  gknServiceDataResponse: GknServiceData,
  chainName: string | undefined,
  chainNetwork?: string | undefined,
): GknServiceData {
  return mergeAllChainSpecificSettings(gknServiceDataResponse, chainName, chainNetwork);
}

export interface GatekeeperNetworkServiceProps {
  gknServiceData: GknServiceData | null;
  requestComplete: boolean;
  burnCooldownPeriodLabel: string;
  burnCooldownPeriodMinutes: number;
  fetchGatekeeperNetwork: Promise<void>;
  queryParams: queryString.ParsedQuery<string>;
  supportsCredentialReuse: boolean | undefined;
  prePassCredentialStorageEnabled: boolean;
  credentialStorageEnabled: boolean;
  showFeesAtStart: boolean;
}

export const GatekeeperNetworkServiceContext = createContext<GatekeeperNetworkServiceProps>(
  {} as GatekeeperNetworkServiceProps,
);

type ConfigProviderProps = {
  children: JSX.Element;
};

export const defaultUniquenessBurnCooldownMinutes = 7 * 24 * 60; // 7 days

/**
 * Retrieve the Gatekeeper Network data from the Gatekeeper Network Service.
 * It also updates the constraints object with any overrides from the Gatekeeper Network Service.
 * @param serviceConfig
 * @param networkAddress
 * @param chain
 * @param chainNetwork
 * @returns GknServiceData
 */
export const fetchGknServiceData = async (
  serviceConfig: ServiceConfig,
  networkAddress: string,
  chain: string,
  chainNetwork?: string,
): Promise<GknServiceData> => {
  // Only public networks can have passes issued through the iframe, so we always use the /public endpoint.
  const gknUrl = `${serviceConfig.gatekeeperNetworkService.endpoint}/public/gatekeeperNetwork/${networkAddress}`;

  const { data: gknData } = await cachedAxios.get(gknUrl);
  return updateConstraintOverrides(gknData, chain as string, chainNetwork as string);
};

const ACTIONS_WITHOUT_CHAIN = ['revoke']; // Special actions that don't require a chain parameter

/**
 * Provider that fetches settings for the current Gatekeeper Network
 * from the Gatekeeper Network Service.
 * @param props
 * @constructor
 */
export function GatekeeperNetworkServiceProvider(props: ConfigProviderProps): JSX.Element {
  const [gknServiceData, setGknServiceData] = useState<GknServiceData | null>(null);
  const [requestComplete, setRequestComplete] = useState<boolean>(false);
  const [requestPromise, setRequestPromise] = useState<Promise<void> | undefined>(undefined);

  const queryParams = queryString.parse(window.location.search);
  const { networkAddress, chain, action, chainNetwork } = queryParams || {};
  const { children } = props;
  const { t } = useTranslation();

  useEffect(() => {
    setRequestComplete(false);
  }, [networkAddress]);

  const serviceConfig = ConfigResolver.getServiceConfig();

  const burnCooldownPeriodMinutes = useMemo(() => {
    if (!gknServiceData) {
      return defaultUniquenessBurnCooldownMinutes;
    }

    const uniquenessCooldownMinutes = getGknValue<number>(gknServiceData, 'uniquenessBurnCooldownMinutes');
    const cooldownPeriodFromConfig = isChainNetworkMainnet(chain as string, chainNetwork as string, networkConfigs)
      ? uniquenessCooldownMinutes
      : getGknValue<number>(gknServiceData, 'testnetUniquenessBurnCooldownMinutes') || uniquenessCooldownMinutes;
    return cooldownPeriodFromConfig ?? defaultUniquenessBurnCooldownMinutes;
  }, [gknServiceData, chain, chainNetwork]);

  const fetchGatekeeperNetwork = async () => {
    const useGknServiceData = await fetchGknServiceData(
      serviceConfig,
      networkAddress as string,
      chain as string,
      chainNetwork as string,
    );
    setGknServiceData(useGknServiceData);
    setRequestComplete(true);
  };

  useEffect(() => {
    if (
      (!ACTIONS_WITHOUT_CHAIN.includes(action as string) || !networkAddress || R.isEmpty(networkAddress)) && // First check if the action doesn't require a chain, and that we have a network address
      (!chain || R.isEmpty(chain)) // If it requires a chain, also check that we have one
    ) {
      return;
    }
    const fetch = fetchGatekeeperNetwork();
    setRequestPromise(fetch);
    fetch.catch((err) => {
      Log.error(`Could not fetch Gatekeeper Network settings from service for ${networkAddress}`, err);
      // Allow the flow to continue, maybe this is a SIP provider flow with a locally-configured SIP template.
      setRequestComplete(true);
    });
  }, [networkAddress, chain]);

  const burnCooldownPeriodLabel = useMemo(() => {
    return formatCooldownPeriod(burnCooldownPeriodMinutes, t);
  }, [burnCooldownPeriodMinutes]);

  const supportsCredentialReuse = useMemo(() => {
    return gknServiceData?.client?.credentialReuseEnabled;
  }, [gknServiceData]);

  const prePassCredentialStorageEnabled = useMemo(() => {
    return !!gknServiceData?.client?.credentialStorageEnabled?.prePassCreation;
  }, [gknServiceData]);

  const credentialStorageEnabled = useMemo(() => {
    return !!gknServiceData?.client?.credentialStorageEnabled;
  }, [gknServiceData]);

  const showFeesAtStart = useMemo(() => {
    return !!gknServiceData?.client?.showFeesAtStart;
  }, [gknServiceData]);

  const value = useMemo(
    () => ({
      gknServiceData,
      burnCooldownPeriodLabel,
      burnCooldownPeriodMinutes,
      requestComplete,
      fetchGatekeeperNetwork: requestPromise || Promise.resolve(),
      queryParams,
      supportsCredentialReuse,
      prePassCredentialStorageEnabled,
      credentialStorageEnabled,
      showFeesAtStart,
    }),
    [
      burnCooldownPeriodLabel,
      burnCooldownPeriodMinutes,
      gknServiceData,
      queryParams,
      requestComplete,
      requestPromise,
      supportsCredentialReuse,
      credentialStorageEnabled,
      prePassCredentialStorageEnabled,
      showFeesAtStart,
    ],
  );
  return <GatekeeperNetworkServiceContext.Provider value={value}>{children}</GatekeeperNetworkServiceContext.Provider>;
}

export const useGKNService = (): GatekeeperNetworkServiceProps => useContext(GatekeeperNetworkServiceContext);
